import { Dispatch, SetStateAction, useMemo } from "react";

import {
  Button,
  ChevronDownSVG,
  ChevronUpSVG,
  CloseSVG,
  Icon,
  type SvgComponentType,
} from "@newsela/angelou";

import {
  getNavMenuButtonAriaProps,
  IButtonAriaProps,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/NavButtonWithPopout/NavButtonWithPopout.utils";
import {
  baseButtonCss,
  closeMenuButtonCss,
  closeMenuIconCss,
  condensedUserButtonVisibilityCss,
  getChevronIconCss,
  getExpandedUserButtonCss,
  getLicensesTextCss,
  getUserInitialsCircleCss,
  getLicenseDropdownCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/UserProfileMenu/UserProfileButton/UserProfileButton.styles";
import { USER_PROFILE_MENU_ID } from "@/base/NewselaGlobalLayoutV2/NavigationBar/UserProfileMenu/UserProfileMenu";
import { getUserInitials } from "@/base/NewselaGlobalLayoutV2/NavigationBar/UserProfileMenu/UserProfileMenu.utils";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { useNavBarItemVisibility } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/hooks/useNavBarItemsVisibilityControl";
import { getLicensesLists } from "../UserProfilePopout/PopoutSubscriptions/PopoutSubscriptions.utils";

const UserProfileButton = ({ isOpen, setIsOpen }: IUserProfileButton) => {
  const { data: user } = useUser();

  const {
    activeScienceLicense,
    activeSSLicense,
    activeELALicense,
    activeEssentialsLicense,
    hasAccessToLite,
    hasNceTrialCampaign,
    isImpersonated,
  } = getUserProperties(user, { shouldUseNewUserApi: false });

  const buttonAriaProps: IButtonAriaProps = useMemo(
    () =>
      getNavMenuButtonAriaProps({
        isOpen,
        id: USER_PROFILE_MENU_ID,
        ariaLabel: "User Profile Menu",
      }),
    [isOpen],
  );

  const [trials, subscriptions] = getLicensesLists(
    hasNceTrialCampaign,
    user?.licenses,
  );
  const isLite = hasAccessToLite && !subscriptions?.length && !trials?.length;

  const licenceShorthands = [
    activeELALicense && "ELA",
    activeSSLicense && "SS",
    activeScienceLicense && "SCI",
    activeEssentialsLicense && "ESS",
    isLite && "Lite",
  ]
    .filter((shorthand) => shorthand)
    .join(" / ");

  const userInitials = getUserInitials(user.name, user.last_name);
  const toggleShowPopout = () => setIsOpen((isOpen) => !isOpen);

  const { isVisible } = useNavBarItemVisibility();

  return (
    <Button
      __cssFor={baseButtonCss}
      onClick={toggleShowPopout}
      flavor={Button.flavors.incognito}
      dataProps={{
        ...buttonAriaProps,
        "data-qa-selector": "global-nav-profile-menu",
      }}
    >
      {isOpen ? (
        <div
          css={closeMenuButtonCss}
          className={USER_PROFILE_MENU_ID}
          data-qa-selector="global-nav-profile-close-menu"
        >
          <Icon
            SvgComponent={CloseSVG as SvgComponentType}
            __cssFor={closeMenuIconCss}
          />
        </div>
      ) : (
        <div
          className={USER_PROFILE_MENU_ID}
          css={condensedUserButtonVisibilityCss(isLite)}
          data-qa-selector="global-nav-user-initials-icon"
        >
          {userInitials}
        </div>
      )}

      <div
        className={USER_PROFILE_MENU_ID}
        css={getExpandedUserButtonCss(isOpen, isLite)}
      >
        <div
          className={USER_PROFILE_MENU_ID}
          css={getUserInitialsCircleCss(isOpen, isLite, isImpersonated)}
          data-qa-selector="global-nav-user-initials"
        >
          {userInitials}
        </div>

        <div css={getLicenseDropdownCss(isVisible("profile-license-abbrs"))}>
          <div
            className={USER_PROFILE_MENU_ID}
            css={getLicensesTextCss(isOpen)}
            data-qa-selector="global-nav-license-shorthands"
          >
            {licenceShorthands}
          </div>

          {isOpen ? (
            <Icon
              SvgComponent={ChevronUpSVG as SvgComponentType}
              __cssFor={getChevronIconCss(isOpen)}
            />
          ) : (
            <Icon
              SvgComponent={ChevronDownSVG as SvgComponentType}
              __cssFor={getChevronIconCss(isOpen)}
            />
          )}
        </div>
      </div>
    </Button>
  );
};

interface IUserProfileButton {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

UserProfileButton.displayName = "UserProfileButton";
export { UserProfileButton };
